import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  BankAccountForSelectFragment,
  BankAccountSelectorDocument,
  BankAccountSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

import { formattedBankAccountNumber } from 'modules/banking/utils'

import type { BankAccountSelectorMultipleProps, BankAccountSelectorSingleProps } from './types'

const isBankAccountSelectorSingleProps = (
  props: BankAccountSelectorSingleProps | BankAccountSelectorMultipleProps
): props is BankAccountSelectorSingleProps => {
  return !props.multiple
}

const BankAccountItem = ({ bankAccount }: { bankAccount: BankAccountForSelectFragment }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={bankAccount.id}>
      <Text.Normal size="300">{accountLabel(bankAccount)}</Text.Normal>
    </Box>
  )
}

const BankAccountSkeleton = () => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center">
      <Skeleton width="s240" height="s32" />
    </Box>
  )
}

const accountLabel = (bankAccount: BankAccountForSelectFragment) => {
  return !!bankAccount.accountAlias
    ? `${bankAccount.accountAlias} - **** ${formattedBankAccountNumber(bankAccount).slice(-4)}`
    : formattedBankAccountNumber(bankAccount)
}

export const BankAccountSelector = (
  props: BankAccountSelectorSingleProps | BankAccountSelectorMultipleProps
) => {
  const {
    error,
    mandatory,
    label,
    emptyLabel,
    providerService,
    accountNumberTypes,
    currency,
    width,
    minWidth,
    alwaysOpen = false,
    disabled = false,
  } = props

  const renderEntity = (entity: BankAccountForSelectFragment) => (
    <BankAccountItem bankAccount={entity} />
  )

  const renderSkeleton = () => <BankAccountSkeleton />

  const variables = {
    providerService,
    accountNumberTypes,
    currency,
  }

  const extractConnection = (data: BankAccountSelectorQuery) => {
    return data.banking.bankAccountsConnection
  }

  const emptyLabelText = (multiple: boolean) => {
    return (
      emptyLabel ||
      i18n.tp('banking.bank_account_selector.empty_label', { count: multiple ? 2 : 1 })
    )
  }

  if (!isBankAccountSelectorSingleProps(props)) {
    return (
      <EntitySelector.Multiple
        label={label}
        disabled={disabled}
        error={error}
        mandatory={mandatory}
        alwaysOpen={alwaysOpen}
        document={BankAccountSelectorDocument}
        variables={variables}
        width={width}
        minWidth={minWidth}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        value={props.value ?? []}
        onChange={props.onSelect}
        renderSkeleton={renderSkeleton}
        renderValue={(entities) => (
          <Text.Normal ellipsis>
            {entities.length > 0
              ? entities.map((el) => accountLabel(el)).join(', ')
              : emptyLabelText(true)}
          </Text.Normal>
        )}
      />
    )
  }

  return (
    <EntitySelector
      label={label}
      disabled={disabled}
      error={error}
      mandatory={mandatory}
      document={BankAccountSelectorDocument}
      variables={variables}
      width={width}
      minWidth={minWidth}
      extractConnection={extractConnection}
      renderEntity={renderEntity}
      value={props.value ?? null}
      onChange={props.onSelect}
      renderSkeleton={renderSkeleton}
      renderValue={(entity) => (
        <Text.Normal>{entity !== null ? accountLabel(entity) : emptyLabelText(false)}</Text.Normal>
      )}
    />
  )
}
